import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { createTheme, alpha } from "@mui/material";
import { projects, PROJECT } from "./projectBranding";

const STORAGE_KEY = "m4d-theme-override";

const ThemeContext = createContext({
  themeName: PROJECT,
  setThemeName: () => {},
});

export const useThemeSwitcher = () => useContext(ThemeContext);

const buildTheme = (palette) =>
  createTheme({
    palette,
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      h4: { fontWeight: 700, letterSpacing: "-0.02em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 600, letterSpacing: "-0.01em" },
      subtitle2: { fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase", fontSize: "0.7rem" },
      body2: { fontSize: "0.835rem" },
      caption: { fontSize: "0.72rem", letterSpacing: "0.03em" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            scrollbarColor: `${alpha(palette.primary.main, 0.3)} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: alpha(palette.primary.main, 0.3),
              borderRadius: 3,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 12,
            border: `1px solid ${alpha(palette.primary.main, 0.08)}`,
            transition: "border-color 0.25s ease, box-shadow 0.25s ease",
            "&:hover": {
              borderColor: alpha(palette.primary.main, 0.2),
              boxShadow: `0 4px 24px ${alpha(palette.primary.main, 0.08)}`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, letterSpacing: "0.01em" },
          sizeSmall: { fontSize: "0.72rem" },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "2px 8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(palette.primary.main, 0.08),
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: "none",
            boxShadow: `1px 0 12px ${alpha("#000", 0.15)}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            boxShadow: `0 1px 0 ${alpha(palette.primary.main, 0.1)}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.75rem",
            fontWeight: 500,
            borderRadius: 6,
          },
        },
      },
    },
  });

export const ThemeSwitcherProvider = ({ children }) => {
  const [themeName, setThemeNameRaw] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && projects[saved] ? saved : PROJECT;
  });

  const setThemeName = useCallback((name) => {
    if (projects[name]) {
      setThemeNameRaw(name);
      localStorage.setItem(STORAGE_KEY, name);
    }
  }, []);

  // Sync theme when another tab/iframe changes localStorage
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && projects[e.newValue]) {
        setThemeNameRaw(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const theme = useMemo(
    () => buildTheme(projects[themeName].palette),
    [themeName]
  );

  const branding = projects[themeName];

  const value = useMemo(
    () => ({ themeName, setThemeName, theme, branding }),
    [themeName, setThemeName, theme, branding]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
